import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/api";
import { Building2, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { addDaysToLocalDateInputValue, toLocalDateInputValue } from "@/utils/date";

export default function CompanyAccount() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAdjust = ["admin", "finance"].includes(
    String(user?.role || "").toLowerCase(),
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [adjustment, setAdjustment] = useState({
    action: "add",
    amount: "",
    adjustment_date: toLocalDateInputValue(),
    notes: "",
  });
  const [postingAdjustment, setPostingAdjustment] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    const today = toLocalDateInputValue();
    const yearStart = `${new Date().getFullYear()}-01-01`;
    Promise.all([
      api.accounting.getCompanyOverview(),
      api.accounting.getBalanceSheet(today),
      api.accounting.getCashFlow(yearStart, today),
    ])
      .then(([overview, bs, cf]) => {
        setData(overview);
        setBalanceSheet(bs);
        setCashFlow(cf);
      })
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const typeMap = (data?.by_account_type || []).reduce((acc, r) => {
    acc[r.account_type] = Number(r.amount || 0);
    return acc;
  }, {});
  const openingMap = (data?.opening_by_party || []).reduce((acc, row) => {
    acc[row.party_type] = Number(row.amount || 0);
    return acc;
  }, {});
  const netReceivable =
    Number(typeMap.receivable || 0) + Number(openingMap.customer || 0);
  const netPayable = Number(typeMap.payable || 0) + Number(openingMap.vendor || 0);
  const cashWithOpening = Number(typeMap.cash || 0) + Number(openingMap.company || 0);
  const customerReceivableDue = Math.max(0, netReceivable);
  const customerAdvanceCredit = Math.max(0, -netReceivable);
  const vendorPayableDue = Math.max(0, netPayable);
  const vendorCreditBalance = Math.max(0, -netPayable);
  const signedBalanceClass = (value) =>
    value < 0 ? "text-emerald-700" : value > 0 ? "text-amber-700" : "text-stone-900";

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="text-secondary" size={28} />
          <h1 className="text-2xl font-heading font-semibold text-secondary">
            Company account
          </h1>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-stone-300 rounded-lg hover:bg-stone-50"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
        {!canAdjust && (
          <button
            type="button"
            onClick={() =>
              navigate(
                `/workflow-tickets?source_type=other&source_id=company-account&to_role=management&title=Company%20account%20review%20required&description=Please%20review%20or%20approve%20company%20account%20adjustment%20request.&due_date=${addDaysToLocalDateInputValue(1)}`,
              )
            }
            className="border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-sm rounded-lg px-3 py-2"
          >
            Generate Review Ticket
          </button>
        )}
      </div>
      <p className="text-sm text-stone-600">
        Ledger balances and recent movement for finance and management visibility.
        Admin and finance can post manual balance adjustments.
      </p>
      <p className="text-xs text-stone-500">
        Note: Vendor payment does not create new expense; expense is recognized at invoice approval.
        Vendor payment only moves balances between payable and cash.
      </p>

      {canAdjust && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 space-y-3">
          <h3 className="font-semibold text-stone-800">Company balance adjustment</h3>
          <p className="text-xs text-stone-500">
            Current balance is not editable directly. Use Add/Withdraw to keep audit trail.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-xs text-stone-600 mb-1">Action</label>
              <select
                value={adjustment.action}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, action: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              >
                <option value="add">Add</option>
                <option value="withdraw">Withdraw</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={adjustment.amount}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, amount: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Date</label>
              <input
                type="date"
                value={adjustment.adjustment_date}
                onChange={(e) =>
                  setAdjustment((prev) => ({
                    ...prev,
                    adjustment_date: e.target.value,
                  }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs text-stone-600 mb-1">Notes</label>
              <input
                type="text"
                value={adjustment.notes}
                onChange={(e) =>
                  setAdjustment((prev) => ({ ...prev, notes: e.target.value }))
                }
                className="w-full border border-stone-300 rounded-lg px-3 py-2"
                placeholder="Reason"
              />
            </div>
            <button
              type="button"
              disabled={postingAdjustment}
              onClick={async () => {
                setPostingAdjustment(true);
                setError("");
                try {
                  await api.accounting.createBalanceAdjustment({
                    party_type: "company",
                    action: adjustment.action,
                    amount: Number(adjustment.amount || 0),
                    adjustment_date: adjustment.adjustment_date,
                    notes: adjustment.notes,
                  });
                  setAdjustment((prev) => ({ ...prev, amount: "", notes: "" }));
                  load();
                } catch (e) {
                  setError(e.message || "Failed to post adjustment");
                } finally {
                  setPostingAdjustment(false);
                }
              }}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {postingAdjustment ? "Posting..." : "Post adjustment"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Income</p>
          <p className="text-lg font-semibold text-stone-900">
            {Number(typeMap.income || 0).toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Expense</p>
          <p className="text-lg font-semibold text-stone-900">
            {Number(typeMap.expense || 0).toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Cash (with opening)</p>
          <p className="text-lg font-semibold text-stone-900">
            {cashWithOpening.toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Net receivable balance</p>
          <p className={`text-lg font-semibold ${signedBalanceClass(netReceivable)}`}>
            {netReceivable.toFixed(2)}
          </p>
          <p className="text-[11px] text-stone-500 mt-1">
            + means customer due, - means customer advance
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Net payable balance</p>
          <p className={`text-lg font-semibold ${signedBalanceClass(netPayable)}`}>
            {netPayable.toFixed(2)}
          </p>
          <p className="text-[11px] text-stone-500 mt-1">
            + means vendor due, - means vendor credit
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Customer receivable due</p>
          <p className="text-lg font-semibold text-amber-700">
            {customerReceivableDue.toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Customer advance credit</p>
          <p className="text-lg font-semibold text-emerald-700">
            {customerAdvanceCredit.toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Vendor payable due</p>
          <p className="text-lg font-semibold text-amber-700">
            {vendorPayableDue.toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-stone-500">Vendor credit balance</p>
          <p className="text-lg font-semibold text-emerald-700">
            {vendorCreditBalance.toFixed(2)}
          </p>
        </div>
      </div>

      {balanceSheet && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <h3 className="font-semibold text-stone-800 mb-2">Statement of Financial Position</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded bg-stone-50 p-3">
              <p className="text-stone-500">Assets</p>
              <p className="font-semibold">{Number(balanceSheet?.totals?.assets || 0).toFixed(2)}</p>
            </div>
            <div className="rounded bg-stone-50 p-3">
              <p className="text-stone-500">Liabilities</p>
              <p className="font-semibold">{Number(balanceSheet?.liabilities?.total_liabilities || 0).toFixed(2)}</p>
            </div>
            <div className="rounded bg-stone-50 p-3">
              <p className="text-stone-500">Equity</p>
              <p className="font-semibold">{Number(balanceSheet?.equity?.total_equity || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {cashFlow && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <h3 className="font-semibold text-stone-800 mb-2">Cash Flow Statement</h3>
          <p className="text-sm text-stone-600">
            Net cash change: {Number(cashFlow?.totals?.net_cash_change || 0).toFixed(2)}
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 font-medium text-stone-800">
          Recent ledger entries
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 text-left text-stone-600">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Description</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_entries || []).map((row) => (
                <tr key={row.id} className="border-t border-stone-100">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.entry_date
                      ? String(row.entry_date).slice(0, 10)
                      : "—"}
                  </td>
                  <td className="px-3 py-2">{row.entry_type}</td>
                  <td className="px-3 py-2">{row.account_type}</td>
                  <td
                    className={`px-3 py-2 text-right font-mono ${
                      Number(row.amount || 0) < 0
                        ? "text-emerald-700"
                        : Number(row.amount || 0) > 0
                          ? "text-amber-700"
                          : "text-stone-700"
                    }`}
                  >
                    {Number(row.amount || 0).toFixed(2)} {row.currency || ""}
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate">
                    {row.description || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data?.recent_entries || data.recent_entries.length === 0) && (
            <p className="px-4 py-6 text-stone-500 text-sm text-center">
              No ledger entries yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
